import type { Framing } from "../protocol/events";

const PROBE_SIZE = 48;
const PIXELS = PROBE_SIZE * PROBE_SIZE;
const BLOCK_SIZE = 8;
const BLOCKS_ACROSS = PROBE_SIZE / BLOCK_SIZE;

/** Exposure-tolerant comparison against a fixed reference, so slow motion accumulates. */
export class ContentChange {
  private reference = new Float32Array(PIXELS);
  private current = new Float32Array(PIXELS);
  private blocks = new Float32Array(BLOCKS_ACROSS * BLOCKS_ACROSS);
  private referenceMean = 0;
  private initialized = false;

  reset(): void {
    this.initialized = false;
  }

  observe(rgba: Uint8ClampedArray): boolean | null {
    if (rgba.length !== PIXELS * 4) {
      this.reset();
      return null;
    }
    let sum = 0;
    for (let i = 0; i < PIXELS; i++) {
      const offset = i * 4;
      const grey = (77 * rgba[offset] + 150 * rgba[offset + 1] + 29 * rgba[offset + 2]) / 256;
      this.current[i] = grey;
      sum += grey;
    }
    const mean = sum / PIXELS;
    if (!this.initialized) {
      this.reference.set(this.current);
      this.referenceMean = mean;
      this.initialized = true;
      return null;
    }

    this.blocks.fill(0);
    let difference = 0;
    const exposure = mean - this.referenceMean;
    for (let y = 0; y < PROBE_SIZE; y++) {
      for (let x = 0; x < PROBE_SIZE; x++) {
        const i = y * PROBE_SIZE + x;
        const delta = Math.abs(this.current[i] - this.reference[i] - exposure);
        difference += delta;
        const block = Math.floor(y / BLOCK_SIZE) * BLOCKS_ACROSS + Math.floor(x / BLOCK_SIZE);
        this.blocks[block] += delta;
      }
    }
    // A local block catches artwork changed within a small, same-position card; the
    // whole-region threshold catches movement that is spread across the image.
    const changed =
      difference / PIXELS > 9 ||
      this.blocks.some((block) => block / (BLOCK_SIZE * BLOCK_SIZE) > 24);
    if (changed) {
      this.reference.set(this.current);
      this.referenceMean = mean;
    }
    return changed;
  }
}

/** One reusable 48x48 2D surface: ~9 KB readback per fresh frame, no extra GL context. */
export class CameraContentProbe {
  private canvas: HTMLCanvasElement | null = null;
  private context: CanvasRenderingContext2D | null = null;
  private change = new ContentChange();

  reset(): void {
    this.change.reset();
  }

  sample(video: HTMLVideoElement, framing: Framing): boolean | null {
    const { window, area } = framing;
    // A 2D probe must not claim to see a projective desk-view crop. Failure simply
    // keeps detection active, which is also the fallback for missing canvas support.
    if (
      window.keystone ||
      area.w <= 0 ||
      area.h <= 0 ||
      ![area.x, area.y, area.w, area.h, ...window.affine].every(Number.isFinite)
    ) {
      this.reset();
      return null;
    }
    try {
      if (!this.canvas) {
        this.canvas = document.createElement("canvas");
        this.canvas.width = this.canvas.height = PROBE_SIZE;
        // Keep video scaling on the GPU; only the tiny destination is read back.
        this.context = this.canvas.getContext("2d", { willReadFrequently: false });
      }
      const context = this.context;
      if (!context) return null;
      const [a, b, tx, c, d, ty] = window.affine;
      const sx = PROBE_SIZE / area.w;
      const sy = PROBE_SIZE / area.h;
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.fillStyle = "black";
      context.fillRect(0, 0, PROBE_SIZE, PROBE_SIZE);
      context.setTransform(
        sx * a,
        sy * b,
        sx * c,
        sy * d,
        -sx * (a * tx + c * ty + area.x),
        -sy * (b * tx + d * ty + area.y),
      );
      context.drawImage(video, 0, 0);
      return this.change.observe(context.getImageData(0, 0, PROBE_SIZE, PROBE_SIZE).data);
    } catch {
      this.reset();
      return null;
    }
  }

  dispose(): void {
    if (this.canvas) this.canvas.width = this.canvas.height = 0;
    this.canvas = this.context = null;
    this.reset();
  }
}
